import { SizeType } from 'antd/es/config-provider/SizeContext';
import React, { FC, memo, useCallback, useEffect, useRef } from 'react';
import { CountrySelector, usePhoneInput } from 'react-international-phone';
import { Input, InputRef } from 'antd';
import classNames from 'classnames';

import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getGlobalSettings } from 'app/appState';
import { CountryType } from 'entities/City';

import 'react-international-phone/style.css';
import s from './index.module.scss';

type InputSize = 'extraSmall' | SizeType;

interface ModernPhoneInputProps {
  value?: Nullable<string>;
  initialValue?: Nullable<string>;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  requiredMark?: boolean;
  disabled?: boolean;
  size?: InputSize;
  bordered?: boolean;
}

export const ModernPhoneInput: FC<ModernPhoneInputProps> = memo((props) => {
  const { placeholder, className, onChange, requiredMark, disabled, size = 'middle', value, initialValue } = props;

  const globalSettings = useAppSelector(getGlobalSettings);

  const inputRef = useRef<InputRef>(null);

  const phoneInput = usePhoneInput({
    defaultCountry: (globalSettings?.country || CountryType.CH).toLowerCase(),
    value: value || initialValue || undefined,
    disableDialCodePrefill: true,
    onChange: (data) => {
      onChange?.(data.phone);
    },
  });

  useEffect(() => {
    if (phoneInput.inputRef && inputRef.current?.input) {
      phoneInput.inputRef.current = inputRef.current.input;
    }
  }, [inputRef, phoneInput.inputRef]);

  const preFillDialCode = useCallback(() => {
    if (!value && !initialValue) {
      phoneInput.setCountry(phoneInput.country.iso2);
    }
  }, [initialValue, value, phoneInput]);

  return (
    <div className={classNames('relative', className)}>
      {requiredMark && <div className="text-error text-2xl absolute z-10 top-0 left-1 pointer-events-none">*</div>}
      <Input
        className={classNames(s.input, s[size])}
        variant="borderless"
        addonBefore={
          <CountrySelector
            selectedCountry={phoneInput.country.iso2}
            onSelect={(country) => {
              phoneInput.setCountry(country.iso2);
            }}
            buttonContentWrapperClassName="w-10"
            buttonClassName="!border-none !bg-accentLight"
            disabled={disabled}
          />
        }
        placeholder={placeholder}
        onChange={phoneInput.handlePhoneValueChange}
        disabled={disabled}
        value={phoneInput.inputValue}
        ref={inputRef}
        onFocus={preFillDialCode}
      />
    </div>
  );
});
